import { Drawer, Flex, Typography, Button, Alert, Input, Space } from "antd";
import Divider from "@mui/material/Divider";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setToggleReviewOrder,
  removeOrderItem,
  setSelectedItem,
  setToggleItemDetails,
  placeOrder,
  clearAll,
} from "../redux/feature/order";
import formatCurrency from "../helpers/formatCurrency";
import axiosInstance from "../helpers/axios";
import { useNavigate } from "react-router-dom";
import { FaUser } from "react-icons/fa";
import { FaPhone, FaLocationDot } from "react-icons/fa6";
import { MdEdit } from "react-icons/md";
import { IoCloseSharp } from "react-icons/io5";

const { Title, Text } = Typography;
const { TextArea } = Input;

const ReviewOrder = ({ toggleReviewOrder }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // const [searchParams] = useSearchParams();

  // const psid = searchParams.get("fbid");

  const { orderItems, finalCustomerDetails, menuDataForEdit, checkoutLoading } =
    useSelector((state) => state.order);

  const [deliveryFee, setDeliveryFee] = useState(50);
  const [distance, setDistance] = useState(0);
  const [hasRider, setHasRider] = useState(false);
  const [riderCheckerLoading, setRiderCheckerLoading] = useState(true);
  const [note, setNote] = useState("");
  const [isLocked, setIsLocked] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [keyError, setKeyError] = useState(false);

  const handleNoteChange = (e) => {
    setNote(e.target.value);
  };

  const onSubmitKey = () => {
    setKeyError(false);

    if (inputValue === "d0rydelivery2026") {
      setIsLocked(false);
    } else {
      setKeyError(true);
    }
  };

  // const convenience = 10;

  const totalPrice = orderItems.reduce((total, item) => {
    return total + Math.ceil(item.price) * item.quantity;
  }, 0);

  const getDeliveryFee = async () => {
    try {
      setRiderCheckerLoading(true);

      const { data } = await axiosInstance().post(`/api/orders/delivery-fee`, {
        merchants_id: finalCustomerDetails.selectedMerchant,
        customer_latitude: finalCustomerDetails.coordinates.latitude,
        customer_longitude: finalCustomerDetails.coordinates.longitude,
        customer_province: finalCustomerDetails.province,
        customer_city: finalCustomerDetails.city,
        pabili_details: finalCustomerDetails.pabiliDetails,
      });

      setDeliveryFee(data.delivery_fee);
      setHasRider(data.hasRider);
      setDistance(data.distance);
      setRiderCheckerLoading(false);
    } catch (error) {
      setRiderCheckerLoading(false);
      console.log(">>> something went wrong fetching delivery fee");
    }
  };

  useEffect(() => {
    const fetchDeliveryFee = async () => {
      if (finalCustomerDetails?.coordinates) {
        await getDeliveryFee();
      }
    };
    fetchDeliveryFee();
  }, [toggleReviewOrder, finalCustomerDetails]);

  const onRemoveOrderItem = (key) => {
    dispatch(removeOrderItem(key));
  };

  const handleClose = () => {
    dispatch(setToggleReviewOrder(false));
  };

  const onEdit = (key, id) => {
    const existingItem = menuDataForEdit.find((item) => item.id === id);

    // Find the current order item to preserve its quantity
    const itemToEdit = orderItems.find((item) => item.key === key);

    dispatch(
      setSelectedItem({
        ...existingItem,
        toBeDeletedKey: key,
        quantity: itemToEdit.quantity,
      }),
    );

    dispatch(setToggleItemDetails(true));
  };

  const onCheckout = async () => {
    try {
      const totalPrice = orderItems.reduce((total, item) => {
        return total + Math.ceil(item.price) * item.quantity;
      }, 0);

      const totalMarkupValue = orderItems.reduce((total, item) => {
        return total + parseFloat(item.total_markup_value);
      }, 0);

      const payload = {
        total_price: parseFloat(totalPrice),
        partner_merchants_id: finalCustomerDetails.selectedMerchant,
        total_markup: parseFloat(totalMarkupValue),
        order: orderItems,
        customerDetails: finalCustomerDetails,
        delivery_fee: deliveryFee,
        distance,
        psid: 1,
        note,
      };

      const resultAction = await dispatch(placeOrder(payload));

      if (placeOrder.fulfilled.match(resultAction)) {
        dispatch(clearAll());
        navigate("/success", {
          state: { orderNumber: resultAction.payload.order_number },
        });
      } else {
        console.error(">>>> order failed:", resultAction);
      }

      // redirect to success screen
    } catch (error) {
      console.log(">>>> something went wrong while placing order");
    }
  };

  return (
    <Drawer
      destroyOnClose
      title="Review Order"
      open={toggleReviewOrder}
      onClose={handleClose}
    >
      {!hasRider && !riderCheckerLoading && (
        <Alert message="No Available Rider" type="error" showIcon />
      )}
      <div style={{ padding: 4 }}>
        <div style={{ marginBottom: 22 }}>
          <Title level={4}>Customer Details</Title>
          <Flex vertical>
            <Flex align="center" gap={8}>
              <FaUser /> <Text>{finalCustomerDetails.fullName}</Text>
            </Flex>
            <Flex align="center" gap={8}>
              <FaPhone /> <Text>{finalCustomerDetails.contactNumber}</Text>
            </Flex>
            <Flex align="center" gap={8}>
              <FaLocationDot />
              <Text>{`${finalCustomerDetails.address}, ${finalCustomerDetails.city}, ${finalCustomerDetails.province}`}</Text>
            </Flex>
          </Flex>
        </div>
        <Divider />
        <div style={{ marginBottom: 12 }}>
          <Title level={4}>Order Details</Title>
          {finalCustomerDetails.selectedMerchant !== 1 ? (
            orderItems.map((item) => {
              return (
                <Flex style={{ marginTop: 12 }} justify="space-between">
                  <div>
                    <div
                      style={{
                        maxWidth: 150,
                      }}
                    >
                      <Text>
                        <span style={{ fontWeight: "bold" }}>
                          {item.quantity}x
                        </span>{" "}
                        {item.title}
                      </Text>
                    </div>

                    {item?.add_ons.length !== 0 && (
                      <ul style={{ margin: 0 }}>
                        {item.variant && <li>{item.variant}</li>}
                        {item?.add_ons.map((ons) => {
                          return <li>{ons.choice_name}</li>;
                        })}
                      </ul>
                    )}
                  </div>
                  <div>
                    <Flex gap={6}>
                      <Text style={{ fontWeight: "bold" }}>
                        {formatCurrency(
                          parseFloat(item.quantity) *
                            parseFloat(Math.ceil(item.price)),
                        )}
                      </Text>
                      {/* <div
                        style={{ backgroundColor: "#3f3f46", borderRadius: 4 }}
                      >
                        <Text
                          type="text"
                          onClick={() => {
                            onEdit(item.key, item.id);
                          }}
                          style={{
                            fontWeight: "bold",
                            padding: 8,
                          }}
                        >
                          <MdEdit style={{ marginTop: 6 }} color="#fff" />
                        </Text>
                      </div> */}
                      <Button
                        type="text"
                        onClick={() => onEdit(item.key, item.id)}
                        style={{
                          backgroundColor: "#3f3f46",
                          borderRadius: 4,
                          padding: 6,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        icon={<MdEdit color="#fff" />}
                      ></Button>
                      {/* <div
                        style={{ backgroundColor: "#fca5a5", borderRadius: 4 }}
                      >
                        <Text
                          type="text"
                          onClick={() => {
                            onRemoveOrderItem(item.key);
                          }}
                          style={{
                            fontWeight: "bold",
                            padding: 8,
                          }}
                        >
                          <IoCloseSharp size={18} style={{ marginTop: 4 }} />
                        </Text>
                      </div> */}
                      <Button
                        type="text"
                        onClick={() => onRemoveOrderItem(item.key)}
                        style={{
                          backgroundColor: "#fca5a5",
                          borderRadius: 4,
                          padding: 6,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        icon={<IoCloseSharp size={18} color="#000" />}
                      />
                    </Flex>
                  </div>
                </Flex>
              );
            })
          ) : (
            <>
              <Text>{finalCustomerDetails?.pabiliDetails?.note}</Text>
            </>
          )}
        </div>
        <br />
        <Divider />
        <br />
        <TextArea
          onChange={handleNoteChange}
          value={note}
          rows={3}
          placeholder={`-- Additional instructions --`}
        />

        {/*  */}
        <Space.Compact style={{ width: "100%", marginTop: 12 }}>
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter Key"
          />
          <Button
            style={{
              borderColor: "#00BF63",
              color: "#00BF63",
              fontWeight: "bold",
            }}
            onClick={onSubmitKey}
          >
            Submit
          </Button>
        </Space.Compact>
        {keyError && <p style={{ color: "red" }}>Incorrect Key</p>}
        {/*  */}

        <div
          style={{
            backgroundColor: "#f3f4f6",
            padding: 16,
            borderRadius: 16,
            marginTop: 12,
          }}
        >
          <Flex vertical>
            {finalCustomerDetails.selectedMerchant !== 1 && (
              <Flex justify="space-between">
                <Text strong>Items</Text>
                <Text strong>{formatCurrency(totalPrice)}</Text>
              </Flex>
            )}

            <Flex justify="space-between">
              <Text strong>Delivery Fee</Text>
              <Text strong>{formatCurrency(deliveryFee)}</Text>
            </Flex>

            {/* <Flex justify="space-between">
              <Text strong>Convience Fee</Text>
              <Text strong>{formatCurrency(convenience)}</Text>
            </Flex> */}

            <Flex justify="space-between">
              <Text strong>Total</Text>
              <Text strong>{formatCurrency(totalPrice + deliveryFee)}</Text>
            </Flex>
          </Flex>
        </div>
      </div>

      <br />
      <div style={{ marginTop: 22 }}>
        <Button
          block
          type="primary"
          onClick={onCheckout}
          disabled={
            !hasRider ||
            checkoutLoading ||
            (finalCustomerDetails.selectedMerchant !== 1 &&
              orderItems.length === 0) ||
            riderCheckerLoading ||
            isLocked
          }
          loading={checkoutLoading}
          style={{
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            padding: 22,
            borderRadius: 12,
            fontWeight: "bold",
            background: "#00BF63",
          }}
        >
          Place Order
        </Button>
      </div>
    </Drawer>
  );
};

export default React.memo(ReviewOrder);
